import { Injectable } from '@angular/core'
import { serializeHttpParams } from '@ftr/api-shared'
import { Region } from '@ftr/contracts/type/court'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult } from '@ftr/foundation'
import {
  RecordingContentSection,
  RecordingContentSectionAvailability,
  RecordingContentSectionAvailabilitySchema,
  RecordingContentSectionSchema,
} from '@ftr/regional-api-contracts'
import { LocalDate } from '@js-joda/core'
import { switchMap } from 'rxjs'
import { z } from 'zod'
import { RegionalApiClientSetupService } from '../api-client'

@Injectable({
  providedIn: 'root',
})
export class RecordingContentSectionsService extends RegionalApiClientSetupService {
  getPublicRecordingContentSections(
    courtSystemId: Uuid,
    region: Region,
    courthouseId: Uuid,
    courtroomId: Uuid,
    date: LocalDate,
  ): ApiResult<RecordingContentSection[]> {
    return this.regionalApiClientForRegion('public/recordings', region).get<RecordingContentSection[]>({
      headers: { courtSystemId },
      path: `/${courtSystemId}/courthouse/${courthouseId}/courtroom/${courtroomId}/content-sections`,
      params: serializeHttpParams({ date: date.toString() }),
      skipAuthentication: true,
      responseBodyParser: z.array(RecordingContentSectionSchema),
    })
  }

  getRecordingContentSections(
    courtSystemId: Uuid,
    courthouseId: Uuid,
    courtroomId: Uuid,
    date: LocalDate,
  ): ApiResult<RecordingContentSection[]> {
    return this.regionalApiClient('recording-content-sections', courtSystemId).pipe(
      switchMap(client =>
        client.get<RecordingContentSection[]>({
          headers: { courtSystemId },
          path: `/${courtSystemId}/courthouse/${courthouseId}/courtroom/${courtroomId}`,
          params: serializeHttpParams({ date: date.toString() }),
          responseBodyParser: z.array(RecordingContentSectionSchema),
        }),
      ),
    )
  }

  getPublicRecordingContentSectionsAvailabilityByDate(
    courtSystemId: Uuid,
    region: Region,
    courthouseId: Uuid,
    courtroomId: Uuid,
    startDate: LocalDate,
    endDate: LocalDate,
  ): ApiResult<RecordingContentSectionAvailability[]> {
    return this.regionalApiClientForRegion('public/recordings', region).get<RecordingContentSectionAvailability[]>({
      headers: { courtSystemId },
      path: `/${courtSystemId}/courthouse/${courthouseId}/courtroom/${courtroomId}/recording-availability`,
      params: serializeHttpParams({ startDate: startDate.toString(), endDate: endDate.toString() }),
      skipAuthentication: true,
      responseBodyParser: z.array(RecordingContentSectionAvailabilitySchema),
    })
  }

  getRecordingContentSectionsAvailabilityByDate(
    courtSystemId: Uuid,
    courthouseId: Uuid,
    courtroomId: Uuid,
    startDate: LocalDate,
    endDate: LocalDate,
  ): ApiResult<RecordingContentSectionAvailability[]> {
    return this.regionalApiClient('recording-content-sections', courtSystemId).pipe(
      switchMap(client =>
        client.get<RecordingContentSectionAvailability[]>({
          headers: { courtSystemId },
          path: `/${courtSystemId}/courthouse/${courthouseId}/courtroom/${courtroomId}/availability`,
          params: serializeHttpParams({ startDate: startDate.toString(), endDate: endDate.toString() }),
          responseBodyParser: z.array(RecordingContentSectionAvailabilitySchema),
        }),
      ),
    )
  }
}
